/**
 * Partial-trip (per-segment) booking maths — pure, dependency-free, so it can
 * be unit-tested and shared by the Worker and the Mini App.
 *
 * A ride's ordered path is `[origin, ...viaCities, destination]`. A sub-trip is
 * an index pair `[fromIdx, toIdx)` with `fromIdx < toIdx`. The legs a sub-trip
 * spans are the consecutive pairs `(i, i+1)` for `i` in `[fromIdx, toIdx)`.
 *
 * Pricing is distance-proportional: a sub-trip costs the full price scaled by
 * its share of the total distance. Because the per-km rate is therefore
 * constant across every sub-trip, a sub-trip's price/reference ratio equals the
 * full trip's — so if the full ride clears the anti-abuse cap, every segment
 * does too, with no extra anti-abuse logic.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** Great-circle distance between two points, in km. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

/** Distance along the ordered sub-path `points[fromIdx..toIdx]` (inclusive
 * endpoints, `fromIdx < toIdx`), summing each consecutive leg. */
export function subtripDistanceKm(points: LatLng[], fromIdx: number, toIdx: number): number {
  let total = 0;
  for (let i = fromIdx; i < toIdx; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (a && b) total += haversineKm(a, b);
  }
  return total;
}

/** Distance of the whole path. */
export function pathDistanceKm(points: LatLng[]): number {
  return subtripDistanceKm(points, 0, Math.max(0, points.length - 1));
}

/**
 * Distance-proportional price for a sub-trip, rounded to the nearest whole
 * currency unit. Falls back to the full price if the total distance is zero
 * (degenerate path). Note: rounding means the segment prices need not sum
 * exactly to the full price — acceptable for cost-sharing.
 */
export function segmentPrice(fullPrice: number, subtripKm: number, totalKm: number): number {
  if (totalKm <= 0) return Math.round(fullPrice);
  return Math.max(0, Math.round((fullPrice * subtripKm) / totalKm));
}

export interface SegmentBooking {
  fromIdx: number;
  toIdx: number;
  seats: number;
}

/** True if a booking's span `[b.fromIdx, b.toIdx)` covers the leg between path
 * index `leg` and `leg + 1`. */
function bookingCoversLeg(b: SegmentBooking, leg: number): boolean {
  return b.fromIdx <= leg && b.toIdx >= leg + 1;
}

/**
 * Seats still available for a requested sub-trip `[fromIdx, toIdx)`, given the
 * ride's total seats and the existing active bookings (each over its own
 * sub-trip). A seat used on `A→Stop` is free again on `Stop→B`, so availability
 * is `seatsTotal − max(occupancy over the legs this sub-trip spans)`.
 */
export function availableForSubtrip(
  seatsTotal: number,
  bookings: SegmentBooking[],
  fromIdx: number,
  toIdx: number,
): number {
  let maxOccupancy = 0;
  for (let leg = fromIdx; leg < toIdx; leg++) {
    let occ = 0;
    for (const b of bookings) {
      if (bookingCoversLeg(b, leg)) occ += b.seats;
    }
    if (occ > maxOccupancy) maxOccupancy = occ;
  }
  return Math.max(0, seatsTotal - maxOccupancy);
}

/**
 * Resolve a `(fromCityId, toCityId)` pair to path indices, validating that both
 * are on the ride's path and ordered. Returns null if invalid.
 */
export function subtripIndices(
  path: string[],
  fromCityId: string,
  toCityId: string,
): { fromIdx: number; toIdx: number } | null {
  const fromIdx = path.indexOf(fromCityId);
  const toIdx = path.indexOf(toCityId);
  if (fromIdx === -1 || toIdx === -1 || fromIdx >= toIdx) return null;
  return { fromIdx, toIdx };
}
